import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Saguaro } from "@/components/Saguaro";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function AddonSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setState("pending"); return; }
    if (attempt >= 8) { setState("pending"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") { setState("paid"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-sm p-8 md:p-10 text-center" data-testid="addon-success-card">
        <Link to="/" className="flex items-center justify-center gap-2 font-serif text-2xl font-semibold text-foreground mb-8">
          <Saguaro className="h-7 w-7 text-primary" /> The East Valley
        </Link>
        {state === "checking" && <div className="py-6 text-muted-foreground"><Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" /> Confirming your payment…</div>}
        {state === "paid" && (
          <div className="py-4">
            <CheckCircle2 className="h-16 w-16 text-secondary mx-auto mb-4" />
            <h1 className="font-serif text-2xl font-semibold text-foreground">You're all set!</h1>
            <p className="text-muted-foreground mt-2">Your add-on is confirmed. We've sent a text confirmation and can't wait to host you.</p>
          </div>
        )}
        {state === "pending" && (
          <div className="py-4">
            <Loader2 className="h-10 w-10 text-primary mx-auto mb-4" />
            <h1 className="font-serif text-2xl font-semibold text-foreground">Payment processing</h1>
            <p className="text-muted-foreground mt-2">This can take a moment. You'll get a text confirmation shortly.</p>
          </div>
        )}
        <Button asChild variant="outline" className="rounded-full mt-6"><Link to="/">Back to site</Link></Button>
      </div>
    </div>
  );
}
